import { useCallback } from 'react';
import type { JournalEntryOwner, MapOwner } from '@roll1/shared';
import type { TranslationKeys } from '../../i18n/types.js';
import { getDefaultData } from '../../lib/journal-utils.js';
import type { StudioCreateRequest } from './useStudioCommands.js';
import type { NoteDraft } from './InlineNoteForm.js';

export interface OwnerActionsDeps {
  readonly create: (request: StudioCreateRequest) => void;
  readonly t: (key: keyof TranslationKeys) => string;
}

/**
 * P03: where a note or a map lives. Every action is a confirmed command: the
 * screen changes when the server's echo (journal:owner-changed /
 * page:owner-changed, matched by request id) arrives, a refusal is shown with
 * «Retry», a lost answer is re-sent after the rejoin (a value, not a toggle).
 */
export function useOwnerActions({ create, t }: OwnerActionsDeps): {
  readonly setEntryOwner: (entryId: string, owner: JournalEntryOwner, name: string) => void;
  readonly setPageOwner: (pageId: string, owner: MapOwner, name: string) => void;
  /** A new note that lives in a beat or at an objective (from the typed form; no draft = a bare one); onCreated gets its id. */
  readonly createNote: (owner: JournalEntryOwner, draft: NoteDraft | undefined, onCreated: (entryId: string) => void) => void;
} {
  const setEntryOwner = useCallback(
    (entryId: string, owner: JournalEntryOwner, name: string): void => {
      create({
        command: 'journal:set-owner',
        echo: 'journal:owner-changed',
        name,
        failTitle: t('studio.owner.failed'),
        payload: { entryId, owner },
        matches: (echo, requestId) => (echo.requestId === requestId && echo.entryId === entryId ? entryId : undefined),
        onCreated: (): void => {},
        recover: 'resend',
      });
    },
    [create, t],
  );

  const setPageOwner = useCallback(
    (pageId: string, owner: MapOwner, name: string): void => {
      create({
        command: 'page:set-owner',
        echo: 'page:owner-changed',
        name,
        failTitle: t('studio.owner.failed'),
        payload: { pageId, owner },
        matches: (echo, requestId) => (echo.requestId === requestId && echo.pageId === pageId ? pageId : undefined),
        onCreated: (): void => {},
        recover: 'resend',
      });
    },
    [create, t],
  );

  const createNote = useCallback(
    (owner: JournalEntryOwner, draft: NoteDraft | undefined, onCreated: (entryId: string) => void): void => {
      const name = draft?.name ?? t('studio.owner.newNoteName');
      const data = draft === undefined ? getDefaultData('lore') : { ...getDefaultData('lore'), content: draft.content, gmNotes: draft.gmNotes };
      create({
        command: 'journal:create',
        echo: 'journal:created',
        name,
        // visibility omitted → the server default 'gm': players never get it.
        payload: { entryType: 'lore', name, data, owner },
        matches: (echo, requestId) =>
          echo.requestId === requestId ? (echo.entry as { id?: string } | undefined)?.id : undefined,
        onCreated,
        recover: 'ask',
      });
    },
    [create, t],
  );

  return { setEntryOwner, setPageOwner, createNote };
}
